/* SuperHoodie – cartoon pet cushion configurator (no app dependency). See sections/sh-main-cushion.liquid for the API contract. */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024;
  var MIN_SIDE = 800;
  var STATUS = {
    upload: 'Upload a photo and your cartoon will appear here.',
    generating: 'Creating your cartoon… this usually takes about 20 seconds.',
    result: 'Here’s your cartoon. Happy with it?',
    approved: 'This is exactly what we’ll print.',
    error: 'Something went wrong while creating your cartoon.'
  };

  function formatMoney(cents, currency) {
    var value = cents / 100;
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currency || 'GBP',
        minimumFractionDigits: value % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(value);
    } catch (e) {
      return '£' + value;
    }
  }

  function wait(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* Copy of the photo scaled to max. 1024 px as JPEG for the image API (faster upload; the original
     still goes with the order). Falls back to the original if the browser can't decode it (e.g. HEIC). */
  function apiPhoto(file, url) {
    return new Promise(function (resolve) {
      var img = new Image();
      img.onload = function () {
        try {
          var scale = Math.min(1, 1024 / Math.max(img.naturalWidth, img.naturalHeight));
          var canvas = document.createElement('canvas');
          canvas.width = Math.round(img.naturalWidth * scale);
          canvas.height = Math.round(img.naturalHeight * scale);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(function (blob) { resolve(blob || file); }, 'image/jpeg', 0.9);
        } catch (e) { resolve(file); }
      };
      img.onerror = function () { resolve(file); };
      img.src = url;
    });
  }

  /* Bot protection (Cloudflare Turnstile), only when the section has a site key. Every cartoon gets a fresh
     token; the widget stays invisible unless Cloudflare wants a click. */
  var turnstileLoad = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (!turnstileLoad) {
      turnstileLoad = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = function () { window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile')); };
        s.onerror = function () { turnstileLoad = null; reject(new Error('turnstile')); };
        document.head.appendChild(s);
      });
    }
    return turnstileLoad;
  }

  function base64ToBlob(b64, type) {
    var bin = atob(b64);
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: type });
  }

  function init(root) {
    if (!root || root.dataset.shReady) return;
    root.dataset.shReady = '1';

    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var variants = [];
    try { variants = JSON.parse($('[data-variants]').textContent); } catch (e) { variants = []; }

    var cfg = {
      endpoint: (root.dataset.endpoint || '').trim(),
      maxTries: parseInt(root.dataset.maxTries, 10) || 3,
      demoImage: root.dataset.demoImage || '',
      cartAdd: root.dataset.cartAdd || '/cart/add.js',
      cartUrl: root.dataset.cartUrl || '/cart',
      productId: root.dataset.productId,
      currency: root.dataset.currency || 'GBP',
      sizeIndex: parseInt(root.dataset.sizeIndex, 10),
      printIndex: parseInt(root.dataset.printIndex, 10),
      turnstile: (root.dataset.turnstile || '').trim()
    };
    var demo = cfg.endpoint === '';

    var el = {
      views: $$('[data-view]'),
      input: $('[data-photo-input]'),
      empty: $('[data-upload-empty]'),
      filled: $('[data-upload-filled]'),
      thumb: $('[data-photo-thumb]'),
      fileName: $('[data-file-name]'),
      change: $('[data-change-photo]'),
      photoMsg: $('[data-photo-msg]'),
      photoLarge: $('[data-photo-large]'),
      photoSmall: $('[data-photo-small]'),
      cartoon: $('[data-cartoon]'),
      versionLabel: $('[data-version-label]'),
      approvedPill: $('[data-approved-pill]'),
      status: $('[data-status]'),
      resultActions: $('[data-result-actions]'),
      approvedActions: $('[data-approved-actions]'),
      approve: $('[data-approve]'),
      retry: $('[data-retry]'),
      tries: $('[data-tries]'),
      unapprove: $('[data-unapprove]'),
      sizeBtns: $$('[data-size]'),
      sizeLabel: $('[data-size-label]'),
      printBtns: $$('[data-print]'),
      printExtras: $$('[data-print-extra]'),
      textToggle: $('[data-text-toggle]'),
      textField: $('[data-text-field]'),
      textInput: $('[data-text-input]'),
      add: $('[data-add]'),
      addLabel: $('[data-add-label]'),
      addMsg: $('[data-add-msg]'),
      addMobile: $('[data-add-mobile]'),
      mbar: $('[data-mbar]'),
      stepUpload: $('[data-cu-step="upload"]'),
      stepCartoon: $('[data-cu-step="cartoon"]'),
      mbarPrice: $('[data-mbar-price]'),
      mbarMeta: $('[data-mbar-meta]'),
      nums: $$('[data-num]'),
      genTitle: $('[data-gen-title]'),
      turnstile: $('[data-turnstile-box]')
    };

    function pressedValue(btns, key) {
      if (!btns.length) return null;
      var on = btns.filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })[0] || btns[0];
      return on.dataset[key];
    }

    var state = {
      stage: 'upload',
      file: null,
      url: null,
      tries: 0,
      cartoonUrl: '',
      cartoonId: '',
      cartoonBlob: null,
      busy: false,
      size: pressedValue(el.sizeBtns, 'size'),
      print: pressedValue(el.printBtns, 'print'),
      errorText: ''
    };

    function findVariant(size, print) {
      return variants.filter(function (v) {
        var okSize = isNaN(cfg.sizeIndex) || cfg.sizeIndex < 0 || v.options[cfg.sizeIndex] === size;
        var okPrint = isNaN(cfg.printIndex) || cfg.printIndex < 0 || v.options[cfg.printIndex] === print;
        return okSize && okPrint;
      })[0] || variants[0];
    }

    function currentVariant() { return findVariant(state.size, state.print); }

    function setMsg(node, text, type) {
      if (!node) return;
      if (!text) { node.hidden = true; node.textContent = ''; return; }
      node.hidden = false;
      node.textContent = text;
      node.classList.toggle('sh-cu-msg--err', type === 'error');
      node.classList.toggle('sh-cu-msg--warn', type === 'warn');
    }

    function showView(stage) {
      var view = 'upload';
      if (stage === 'generating') view = 'generating';
      if (stage === 'result' || stage === 'approved') view = 'result';
      if (stage === 'error') view = state.cartoonUrl ? 'result' : (state.url ? 'generating' : 'upload');
      el.views.forEach(function (v) { v.hidden = v.dataset.view !== view; });
      root.classList.toggle('is-error', stage === 'error');
      if (el.genTitle) el.genTitle.textContent = stage === 'error' ? 'We couldn’t create your cartoon' : 'Creating your cartoon…';
    }

    function render() {
      var s = state.stage;
      showView(s);
      el.status.textContent = s === 'error' ? (state.errorText || STATUS.error) : STATUS[s];
      var hasResult = (s === 'result') && !!state.cartoonUrl;
      el.resultActions.hidden = !hasResult && s !== 'error';
      el.approvedActions.hidden = s !== 'approved';
      el.approvedPill.hidden = s !== 'approved';
      el.approve.hidden = s === 'error';
      var left = cfg.maxTries - state.tries;
      el.retry.disabled = s === 'error' ? false : left <= 0;
      el.tries.textContent = s === 'error' ? '' : (left <= 0 ? 'No tries left for this photo – upload a different photo to start again.' : (left === 1 ? '1 try left for this photo' : left + ' tries left for this photo'));
      if (el.versionLabel) el.versionLabel.textContent = 'Your cartoon · version ' + Math.max(1, state.tries) + (demo ? ' (example)' : '');
      if (el.nums[0]) el.nums[0].classList.toggle('is-done', !!state.file);
      if (el.nums[1]) el.nums[1].classList.toggle('is-done', s === 'approved');
      el.empty.hidden = !!state.file;
      el.filled.hidden = !state.file;
      updatePrice();
    }

    function updatePrice() {
      var v = currentVariant();
      if (!v) return;
      var ready = state.stage === 'approved' && !state.busy;
      var price = formatMoney(v.price, cfg.currency);
      el.add.disabled = !ready;
      el.add.dataset.variant = v.id;
      el.addLabel.textContent = ready ? 'Add to basket · ' + price : (state.busy ? 'Adding…' : 'Approve your cartoon to continue');
      if (el.addMobile) {
        // Not ready yet: the bar button takes you to the missing step instead of being greyed out
        el.addMobile.disabled = state.busy || state.stage === 'generating';
        el.addMobile.textContent = ready ? 'Add to basket'
          : state.stage === 'upload' ? 'Add your photo'
          : state.stage === 'generating' ? 'Creating cartoon…'
          : state.stage === 'error' ? 'Back to your cartoon'
          : state.busy ? 'Adding…' : 'Approve your cartoon';
      }
      if (el.mbarPrice) el.mbarPrice.textContent = price;
      var sizeBtn = el.sizeBtns.filter(function (b) { return b.dataset.size === state.size; })[0];
      var sizeText = sizeBtn ? sizeBtn.dataset.label : '';
      if (el.sizeLabel) el.sizeLabel.textContent = sizeText;
      var printText = '';
      el.printBtns.forEach(function (b) { if (b.dataset.print === state.print) printText = b.textContent.trim(); });
      if (el.mbarMeta) el.mbarMeta.textContent = sizeText + (printText ? ' · ' + printText.replace(/\+.*$/, '').trim() : '');
      // price difference for the second print option
      if (el.printBtns.length > 1) {
        var base = findVariant(state.size, el.printBtns[0].dataset.print);
        el.printBtns.forEach(function (b, i) {
          var x = el.printExtras[i];
          if (!x) return;
          if (i === 0) { x.textContent = ''; return; }
          var other = findVariant(state.size, b.dataset.print);
          var diff = other && base ? other.price - base.price : 0;
          x.textContent = diff > 0 ? ' +' + formatMoney(diff, cfg.currency) : '';
        });
      }
    }

    function clearCartoon() {
      if (state.cartoonBlob && state.cartoonUrl) { try { URL.revokeObjectURL(state.cartoonUrl); } catch (e) {} }
      state.cartoonUrl = '';
      state.cartoonId = '';
      state.cartoonBlob = null;
    }

    function setPhoto(file, url) {
      if (state.url) { try { URL.revokeObjectURL(state.url); } catch (e) {} }
      state.file = file;
      state.url = url;
      state.tries = 0;
      clearCartoon();
      el.fileName.textContent = file ? file.name : '';
      [el.thumb, el.photoLarge, el.photoSmall].forEach(function (img) { if (img && url) img.src = url; });
    }

    function checkFile(file) {
      return new Promise(function (resolve) {
        var isImage = /^image\//.test(file.type) || /\.(heic|heif)$/i.test(file.name);
        if (!isImage) return resolve({ ok: false, msg: 'Please choose a photo (JPG, PNG or HEIC).' });
        if (file.size > MAX_BYTES) return resolve({ ok: false, msg: 'This photo is larger than 20 MB. Please choose a smaller one.' });
        var url = URL.createObjectURL(file);
        var img = new Image();
        img.onload = function () {
          var small = Math.min(img.naturalWidth, img.naturalHeight) < MIN_SIDE;
          resolve({ ok: true, url: url, warn: small ? 'This photo is quite small, so your cartoon may look less sharp. A larger photo works best.' : '' });
        };
        img.onerror = function () {
          // e.g. HEIC in browsers that can't display it – still allowed
          resolve({ ok: true, url: url, warn: 'We can’t show a preview of this file type, but we can still use it.' });
        };
        img.src = url;
      });
    }

    var turnstile = { id: null, pending: null };
    var TURNSTILE_ERROR = 'We couldn’t check your browser just now. Please reload the page and try again.';

    function turnstileToken() {
      if (!cfg.turnstile || !el.turnstile) return Promise.resolve('');
      return loadTurnstile().then(function (ts) {
        return new Promise(function (resolve, reject) {
          var timer = setTimeout(function () { finish(new Error(TURNSTILE_ERROR)); }, 60000);
          function finish(err, token) {
            clearTimeout(timer);
            turnstile.pending = null;
            if (err) reject(err); else resolve(token);
          }
          turnstile.pending = finish;
          if (turnstile.id === null) {
            turnstile.id = ts.render(el.turnstile, {
              sitekey: cfg.turnstile,
              action: 'cartoon',
              appearance: 'interaction-only',
              execution: 'execute',
              retry: 'never',
              callback: function (token) { if (turnstile.pending) turnstile.pending(null, token); },
              'error-callback': function () { if (turnstile.pending) turnstile.pending(new Error(TURNSTILE_ERROR)); return true; },
              'expired-callback': function () {}
            });
          } else {
            ts.reset(turnstile.id);
          }
          ts.execute(turnstile.id);
        });
      }, function () {
        throw new Error(TURNSTILE_ERROR);
      });
    }

    function generate() {
      var v = currentVariant();
      if (demo) {
        return wait(2200).then(function () {
          return { image_url: cfg.demoImage, id: 'demo-' + Date.now() };
        });
      }
      return Promise.all([apiPhoto(state.file, state.url), turnstileToken()]).then(function (r) {
        var photo = r[0];
        var fd = new FormData();
        fd.append('photo', photo, photo === state.file ? state.file.name : 'photo.jpg');
        fd.append('product_id', cfg.productId);
        if (v) fd.append('variant_id', v.id);
        if (r[1]) fd.append('turnstile', r[1]);
        return fetch(cfg.endpoint, { method: 'POST', body: fd });
      }).then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          var b64 = json.data && json.data[0] && json.data[0].b64_json;
          if (!res.ok || !(json.image_url || b64)) {
            throw new Error(json.error || 'We couldn’t create your cartoon just now. Please try again.');
          }
          if (json.image_url) return json;
          // Image comes back as data: keep it as a file, it is uploaded with the order (no storage on our side)
          var blob = base64ToBlob(b64, 'image/png');
          return { image_url: URL.createObjectURL(blob), blob: blob, id: '' };
        });
      });
    }

    function startGeneration() {
      if (!state.file || state.busy) return;
      if (state.tries >= cfg.maxTries) return;
      state.busy = true;
      state.stage = 'generating';
      render();
      generate().then(function (json) {
        state.tries += 1;
        clearCartoon();
        state.cartoonUrl = json.image_url;
        state.cartoonId = json.id || '';
        state.cartoonBlob = json.blob || null;
        el.cartoon.src = json.image_url;
        state.stage = 'result';
      }).catch(function (err) {
        state.errorText = (err && err.message) ? err.message : STATUS.error;
        state.stage = 'error';
      }).then(function () {
        state.busy = false;
        render();
      });
    }

    el.input.addEventListener('change', function () {
      var file = el.input.files && el.input.files[0];
      if (!file) return;
      checkFile(file).then(function (res) {
        if (!res.ok) {
          setMsg(el.photoMsg, res.msg, 'error');
          el.input.value = '';
          return;
        }
        setMsg(el.photoMsg, res.warn, res.warn ? 'warn' : null);
        setPhoto(file, res.url);
        startGeneration();
      });
    });

    el.change.addEventListener('click', function () {
      setPhoto(null, null);
      el.input.value = '';
      setMsg(el.photoMsg, '');
      state.stage = 'upload';
      render();
      el.input.click();
    });

    el.retry.addEventListener('click', function () {
      if (state.stage === 'error') {
        state.stage = state.cartoonUrl ? 'result' : 'upload';
        // retry without spending a try
        startGeneration();
        return;
      }
      startGeneration();
    });

    el.approve.addEventListener('click', function () {
      if (!state.cartoonUrl) return;
      state.stage = 'approved';
      render();
    });

    el.unapprove.addEventListener('click', function () {
      state.stage = 'result';
      render();
    });

    el.sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.size = btn.dataset.size;
        el.sizeBtns.forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        updatePrice();
      });
    });

    el.printBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.print = btn.dataset.print;
        el.printBtns.forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        updatePrice();
      });
    });

    if (el.textToggle) {
      el.textToggle.addEventListener('change', function () {
        el.textField.hidden = !el.textToggle.checked;
        if (el.textToggle.checked) el.textInput.focus();
      });
    }

    function addToBasket() {
      if (state.stage !== 'approved' || state.busy) return;
      var v = currentVariant();
      if (!v) return;
      state.busy = true;
      setMsg(el.addMsg, '');
      updatePrice();
      var fd = new FormData();
      fd.append('id', v.id);
      fd.append('quantity', '1');
      if (state.file) fd.append('properties[Photo]', state.file, state.file.name);
      if (demo) fd.append('properties[Cartoon]', 'DEMO – not generated');
      else if (state.cartoonBlob) fd.append('properties[Cartoon]', state.cartoonBlob, 'cartoon.png');
      else fd.append('properties[Cartoon]', state.cartoonUrl);
      if (state.cartoonId) fd.append('properties[_cartoon_id]', state.cartoonId);
      var text = el.textToggle && el.textToggle.checked && el.textInput ? el.textInput.value.trim() : '';
      if (text) fd.append('properties[Name or text]', text.slice(0, 20));
      fetch(cfg.cartAdd, { method: 'POST', body: fd, headers: { Accept: 'application/json' } })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (json) {
            if (!res.ok || json.status) throw new Error(json.description || json.message || 'Sorry, we couldn’t add this to your basket. Please try again.');
            // Klaviyo and Elevar need a moment to send "Added to Cart" (snippets/sh-added-to-cart.liquid).
            return (window.shAddedToCart ? window.shAddedToCart() : Promise.resolve()).then(function () {
              window.location.href = cfg.cartUrl;
            });
          });
        })
        .catch(function (err) {
          state.busy = false;
          setMsg(el.addMsg, err.message, 'error');
          updatePrice();
        });
    }

    el.add.addEventListener('click', addToBasket);
    if (el.addMobile) {
      el.addMobile.addEventListener('click', function () {
        if (state.stage === 'approved') return addToBasket();
        var step = state.stage === 'upload' ? el.stepUpload : el.stepCartoon;
        if (step) step.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    // Mobile bar: only while the main button is still below the screen
    if (el.mbar && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        var entry = entries[0];
        el.mbar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top > 0);
      }).observe(el.add);
    }

    if (cfg.turnstile && !demo) loadTurnstile().catch(function () {});
    render();
  }

  function boot(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-sh-cushion]'), init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(); });
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
